(() => {
  const selector = 'img[data-vd-sticker-artwork], [data-vd-sticker-card] img';
  const measurements = new WeakMap();
  const cache = new Map();
  const resize = new ResizeObserver(entries => entries.forEach(entry => fit(entry.target)));

  function fit(img) {
    const bounds = measurements.get(img);
    if (!bounds || !img.clientWidth || !img.clientHeight) return;
    const width = img.clientWidth;
    const height = img.clientHeight;
    const contained = Math.min(width / bounds.width, height / bounds.height);
    const scale = Math.max(1, Math.min(2.5, width * 0.92 / (bounds.inkWidth * contained), height * 0.92 / (bounds.inkHeight * contained)));
    if (scale < 1.04) {
      img.dataset.vdArtworkFit = 'unchanged';
      return;
    }
    const x = -(bounds.centerX - bounds.width / 2) * contained * scale / width * 100;
    const y = -(bounds.centerY - bounds.height / 2) * contained * scale / height * 100;
    img.style.setProperty('--vd-artwork-scale', scale.toFixed(4));
    img.style.setProperty('--vd-artwork-x', `${x.toFixed(3)}%`);
    img.style.setProperty('--vd-artwork-y', `${y.toFixed(3)}%`);
    img.dataset.vdArtworkFit = 'ready';
    if (img.srcset) img.sizes = `${Math.ceil(width * scale)}px`;
  }

  function measure(url) {
    if (cache.has(url)) return cache.get(url);
    const promise = new Promise(resolve => {
      const source = new Image();
      source.crossOrigin = 'anonymous';
      source.onerror = () => resolve(null);
      source.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ratio = source.naturalWidth / source.naturalHeight;
          canvas.width = ratio >= 1 ? 128 : Math.max(1, Math.round(128 * ratio));
          canvas.height = ratio >= 1 ? Math.max(1, Math.round(128 / ratio)) : 128;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(source, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
          let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
          for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
              const i = (y * canvas.width + x) * 4;
              if (pixels[i + 3] < 20 || Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) > 242) continue;
              left = Math.min(left, x); right = Math.max(right, x);
              top = Math.min(top, y); bottom = Math.max(bottom, y);
            }
          }
          if (right < left || bottom < top) return resolve(null);
          left = Math.max(0, left - 4); right = Math.min(canvas.width - 1, right + 4);
          top = Math.max(0, top - 4); bottom = Math.min(canvas.height - 1, bottom + 4);
          resolve({ width: canvas.width, height: canvas.height, inkWidth: right - left + 1, inkHeight: bottom - top + 1, centerX: (left + right + 1) / 2, centerY: (top + bottom + 1) / 2 });
        } catch {
          resolve(null);
        }
      };
      source.src = url;
    });
    cache.set(url, promise);
    return promise;
  }

  async function prepare(img) {
    const bounds = await measure(img.currentSrc || img.src);
    if (!bounds || !img.isConnected) {
      img.dataset.vdArtworkFit = 'unchanged';
      return;
    }
    measurements.set(img, bounds);
    fit(img);
    resize.observe(img);
  }

  const visible = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      visible.unobserve(img);
      if (img.complete && img.naturalWidth) prepare(img);
      else img.addEventListener('load', () => prepare(img), { once: true });
    });
  }, { rootMargin: '200px' });

  function scan() {
    document.querySelectorAll(selector).forEach(img => {
      if (img.dataset.vdArtworkFit) return;
      img.setAttribute('data-vd-sticker-artwork', '');
      img.dataset.vdArtworkFit = 'pending';
      visible.observe(img);
    });
  }
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
  scan();
})();
